import type { LancarPOSDatabase } from '@lancar-pos/db-schema';
import type { ServiceAccountCredentials } from './googleSheetsApi';
import {
  getAccessToken,
  fetchSheetRows,
  appendSheetRows,
  updateSheetRow,
  findRowById,
  ensureSheetHeaders,
} from './googleSheetsApi';
import {
  productToRow,
  rowToProduct,
  categoryToRow,
  rowToCategory,
  orderToRow,
  rowToOrder,
  customerToRow,
  rowToCustomer,
  PRODUCT_HEADERS,
  CATEGORY_HEADERS,
  ORDER_HEADERS,
  CUSTOMER_HEADERS,
} from './syncMappers';
import {
  addToQueue,
  removeFromQueue,
  incrementAttempts,
  getPendingItems,
  getQueueStats,
  retryFailedItems,
} from './syncQueue';

export interface SyncResult {
  collection: string;
  pulled: number;
  pushed: number;
  errors: string[];
}

export interface SyncStatus {
  isSyncing: boolean;
  lastSyncAt: string | null;
  error: string | null;
  queueStats: { pending: number; failed: number; total: number };
}

type SyncEventType = 'start' | 'complete' | 'error' | 'progress';
type SyncEventCallback = (status: SyncStatus) => void;

const GLOBAL_SYNC_META_ID = 'global_sync_state';

class SyncService {
  private db: LancarPOSDatabase | null = null;
  private credentials: ServiceAccountCredentials | null = null;
  private spreadsheetId: string | null = null;
  private accessToken: string | null = null;
  private isSyncing = false;
  private lastSyncAt: string | null = null;
  private error: string | null = null;
  private eventListeners: Map<SyncEventType, Set<SyncEventCallback>> = new Map();
  private initialized = false;

  async setDatabase(db: LancarPOSDatabase) {
    this.db = db;
    if (!this.initialized) {
      await this.loadSyncState();
      this.initialized = true;
    }
  }

  setCredentials(credentials: ServiceAccountCredentials, spreadsheetId: string) {
    this.credentials = credentials;
    this.spreadsheetId = spreadsheetId;
  }

  clearCredentials() {
    this.credentials = null;
    this.spreadsheetId = null;
    this.accessToken = null;
  }

  private async loadSyncState(): Promise<void> {
    if (!this.db) return;
    
    try {
      const meta = await this.db.sync_meta.findOne(GLOBAL_SYNC_META_ID).exec();
      if (meta && meta.lastSyncAt) {
        this.lastSyncAt = meta.lastSyncAt;
      }
    } catch (error) {
      console.warn('Failed to load sync state:', error);
    }
  }

  private async persistSyncState(): Promise<void> {
    if (!this.db || !this.lastSyncAt) return;
    
    try {
      await this.db.sync_meta.upsert({
        id: GLOBAL_SYNC_META_ID,
        collectionName: 'global',
        lastSyncAt: this.lastSyncAt,
        lastCheckpoint: null,
        status: 'idle',
        errorMessage: null,
        recordCount: 0,
      });
    } catch (error) {
      console.warn('Failed to persist sync state:', error);
    }
  }

  async persistLastSyncAt(lastSyncAt: string): Promise<void> {
    this.lastSyncAt = lastSyncAt;
    await this.persistSyncState();
  }

  getStatus(): SyncStatus {
    return {
      isSyncing: this.isSyncing,
      lastSyncAt: this.lastSyncAt,
      error: this.error,
      queueStats: getQueueStats(),
    };
  }

  on(event: SyncEventType, callback: SyncEventCallback): () => void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event)!.add(callback);
    
    return () => {
      this.eventListeners.get(event)?.delete(callback);
    };
  }

  private emit(event: SyncEventType, status: SyncStatus) {
    const callbacks = this.eventListeners.get(event);
    if (callbacks) {
      callbacks.forEach(cb => cb(status));
    }
  }

  private async getOrCreateAccessToken(): Promise<string> {
    if (!this.credentials) {
      throw new Error('Credentials not configured');
    }
    
    if (!this.accessToken) {
      this.accessToken = await getAccessToken(this.credentials);
    }
    
    return this.accessToken;
  }

  async syncAll(): Promise<SyncResult[]> {
    if (this.isSyncing) {
      throw new Error('Sync already in progress');
    }

    if (!this.db || !this.credentials || !this.spreadsheetId) {
      throw new Error('Sync not configured');
    }

    this.isSyncing = true;
    this.error = null;
    this.emit('start', this.getStatus());

    try {
      this.accessToken = await this.getOrCreateAccessToken();

      await this.ensureAllHeaders();

      await this.processQueue();

      const results: SyncResult[] = [];
      
      const collections = ['categories', 'products', 'customers', 'orders'] as const;
      
      for (const collection of collections) {
        const result = await this.syncCollection(collection);
        results.push(result);
        this.emit('progress', this.getStatus());
      }

      this.lastSyncAt = new Date().toISOString();
      await this.persistSyncState();
      this.emit('complete', this.getStatus());
      
      return results;
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'Sync failed';
      this.emit('error', this.getStatus());
      throw error;
    } finally {
      this.isSyncing = false;
    }
  }

  async pullFromSheets(): Promise<SyncResult[]> {
    if (this.isSyncing) {
      throw new Error('Sync already in progress');
    }

    if (!this.db || !this.credentials || !this.spreadsheetId) {
      throw new Error('Sync not configured');
    }

    this.isSyncing = true;
    this.error = null;
    this.emit('start', this.getStatus());

    try {
      this.accessToken = await this.getOrCreateAccessToken();

      await this.ensureAllHeaders();

      const results: SyncResult[] = [];
      
      const collections = ['categories', 'products', 'customers', 'orders'] as const;
      
      for (const collection of collections) {
        const result = await this.pullCollection(collection);
        results.push(result);
        this.emit('progress', this.getStatus());
      }

      this.lastSyncAt = new Date().toISOString();
      await this.persistSyncState();
      this.emit('complete', this.getStatus());
      
      return results;
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'Pull failed';
      this.emit('error', this.getStatus());
      throw error;
    } finally {
      this.isSyncing = false;
    }
  }

  async pushToSheets(): Promise<SyncResult[]> {
    if (this.isSyncing) {
      throw new Error('Sync already in progress');
    }

    if (!this.db || !this.credentials || !this.spreadsheetId) {
      throw new Error('Sync not configured');
    }

    this.isSyncing = true;
    this.error = null;
    this.emit('start', this.getStatus());

    try {
      this.accessToken = await this.getOrCreateAccessToken();

      await this.ensureAllHeaders();

      const results: SyncResult[] = [];
      
      const collections = ['categories', 'products', 'customers', 'orders'] as const;
      
      for (const collection of collections) {
        const result = await this.pushCollection(collection);
        results.push(result);
        this.emit('progress', this.getStatus());
      }

      this.lastSyncAt = new Date().toISOString();
      await this.persistSyncState();
      this.emit('complete', this.getStatus());
      
      return results;
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'Push failed';
      this.emit('error', this.getStatus());
      throw error;
    } finally {
      this.isSyncing = false;
    }
  }

  retryFailed(): void {
    retryFailedItems();
  }

  private async ensureAllHeaders(): Promise<void> {
    if (!this.accessToken || !this.spreadsheetId) return;

    await Promise.all([
      ensureSheetHeaders(this.accessToken, this.spreadsheetId, 'Products', PRODUCT_HEADERS),
      ensureSheetHeaders(this.accessToken, this.spreadsheetId, 'Categories', CATEGORY_HEADERS),
      ensureSheetHeaders(this.accessToken, this.spreadsheetId, 'Orders', ORDER_HEADERS),
      ensureSheetHeaders(this.accessToken, this.spreadsheetId, 'Customers', CUSTOMER_HEADERS),
    ]);
  }

  private async syncCollection(collection: 'products' | 'categories' | 'customers' | 'orders'): Promise<SyncResult> {
    const pullResult = await this.pullCollection(collection);
    const pushResult = await this.pushCollection(collection);
    
    return {
      collection,
      pulled: pullResult.pulled,
      pushed: pushResult.pushed,
      errors: [...pullResult.errors, ...pushResult.errors],
    };
  }

  private async pullCollection(collection: 'products' | 'categories' | 'customers' | 'orders'): Promise<SyncResult> {
    const result: SyncResult = { collection, pulled: 0, pushed: 0, errors: [] };
    
    if (!this.db || !this.accessToken || !this.spreadsheetId) return result;

    try {
      const sheetName = collection.charAt(0).toUpperCase() + collection.slice(1);
      const rows = await fetchSheetRows(this.accessToken, this.spreadsheetId, sheetName);

      for (const row of rows) {
        if (!row.id || row._deleted === 'true') continue;

        try {
          await this.processRowForPull(collection, row);
          result.pulled++;
        } catch (error) {
          result.errors.push(`Failed to pull ${row.id}: ${error}`);
        }
      }
    } catch (error) {
      result.errors.push(`Failed to fetch ${collection}: ${error}`);
    }

    return result;
  }

  private async processRowForPull(collection: string, row: Record<string, string>): Promise<void> {
    if (!this.db) return;

    const localDoc = await this.db[collection as keyof LancarPOSDatabase]
      .findOne(row.id)
      .exec();

    const sheetUpdatedAt = new Date(row.updated_at || 0);
    const localUpdatedAt = localDoc ? new Date((localDoc as any).updatedAt) : null;

    if (!localDoc || (sheetUpdatedAt > (localUpdatedAt || 0))) {
      const docData = this.rowToDocument(collection, row);
      
      if (localDoc) {
        await (this.db[collection as keyof LancarPOSDatabase] as any)
          .upsert(docData);
      } else {
        await (this.db[collection as keyof LancarPOSDatabase] as any)
          .insert(docData);
      }
    }
  }

  private async pushCollection(collection: 'products' | 'categories' | 'customers' | 'orders'): Promise<SyncResult> {
    const result: SyncResult = { collection, pulled: 0, pushed: 0, errors: [] };
    
    if (!this.db || !this.accessToken || !this.spreadsheetId) return result;

    try {
      const lastSync = this.lastSyncAt ? new Date(this.lastSyncAt) : null;
      
      const query = lastSync
        ? (this.db[collection as keyof LancarPOSDatabase] as any)
            .find()
            .where('updatedAt')
            .gt(lastSync.toISOString())
        : (this.db[collection as keyof LancarPOSDatabase] as any).find();

      const docs = await query.exec();

      for (const doc of docs) {
        const docData = doc.toJSON();
        if ((docData as any)._deleted) continue;

        try {
          await this.processDocForPush(collection, docData);
          result.pushed++;
        } catch (error) {
          addToQueue(
            collection,
            (docData as any).id,
            'update',
            docData as Record<string, unknown>
          );
          result.errors.push(`Failed to push ${(docData as any).id}: ${error}`);
        }
      }
    } catch (error) {
      result.errors.push(`Failed to push ${collection}: ${error}`);
    }

    return result;
  }

  private async processDocForPush(collection: string, doc: Record<string, unknown>): Promise<void> {
    if (!this.accessToken || !this.spreadsheetId) return;

    const sheetName = collection.charAt(0).toUpperCase() + collection.slice(1);
    const row = this.documentToRow(collection, doc);
    const headers = this.getHeaders(collection);

    const existing = await findRowById(this.accessToken, this.spreadsheetId, sheetName, row.id);

    if (!existing) {
      await appendSheetRows(this.accessToken, this.spreadsheetId, sheetName, [row], headers);
    } else {
      const docUpdatedAt = new Date(doc.updatedAt as string);
      const sheetUpdatedAt = new Date(existing.data.updated_at || 0);

      if (docUpdatedAt > sheetUpdatedAt) {
        await updateSheetRow(this.accessToken, this.spreadsheetId, sheetName, existing.rowNumber, row, headers);
      }
    }
  }

  private async processQueue(): Promise<void> {
    const pendingItems = getPendingItems();

    for (const item of pendingItems) {
      try {
        await this.processDocForPush(item.collection, item.data);
        removeFromQueue(item.id);
      } catch (error) {
        incrementAttempts(item.id);
      }
    }
  }

  private documentToRow(collection: string, doc: Record<string, unknown>): Record<string, string> {
    switch (collection) {
      case 'products':
        return productToRow(doc as any);
      case 'categories':
        return categoryToRow(doc as any);
      case 'orders':
        return orderToRow(doc as any);
      case 'customers':
        return customerToRow(doc as any);
      default:
        throw new Error(`Unknown collection: ${collection}`);
    }
  }

  private rowToDocument(collection: string, row: Record<string, string>): Record<string, unknown> {
    switch (collection) {
      case 'products':
        return rowToProduct(row) as unknown as Record<string, unknown>;
      case 'categories':
        return rowToCategory(row) as unknown as Record<string, unknown>;
      case 'orders':
        return rowToOrder(row) as unknown as Record<string, unknown>;
      case 'customers':
        return rowToCustomer(row) as unknown as Record<string, unknown>;
      default:
        throw new Error(`Unknown collection: ${collection}`);
    }
  }

  private getHeaders(collection: string): string[] {
    switch (collection) {
      case 'products':
        return PRODUCT_HEADERS;
      case 'categories':
        return CATEGORY_HEADERS;
      case 'orders':
        return ORDER_HEADERS;
      case 'customers':
        return CUSTOMER_HEADERS;
      default:
        return [];
    }
  }
}

export const syncService = new SyncService();
